export class RatesSearch {
    constructor(ratesSearchEl) {
        this.searching = false;
        this.ratesSearchEl = ratesSearchEl;

        this.refine = ratesSearchEl.querySelector(".refine");
        this.rates = ratesSearchEl.querySelector(".rates");
        this.back = ratesSearchEl.querySelector(".back");
        this.next = ratesSearchEl.querySelector(".next");
        this.prev = ratesSearchEl.querySelector(".prev");

        this.ratesLoading = this.rates.querySelector(".loading");
        this.ratesDetail = this.rates.querySelector(".details");

        this.noitems = ratesSearchEl.dataset.noitems;
        this.form = ratesSearchEl.querySelector("form");
        this.addresses = JSON.parse(ratesSearchEl.dataset.addresses || '[]');
        this.abbreviations = JSON.parse(ratesSearchEl.dataset.abbrevs || '{}');

        this.searchBy = ratesSearchEl.querySelector("select[name='searchBy']");
        this.streetSearch = ratesSearchEl.querySelector(".streetSearch");
        this.streetNumber = ratesSearchEl.querySelector("input[name='streetNumber']");
        this.streetName = ratesSearchEl.querySelector("input[name='streetName']");

        this.valuationNumberSearch = ratesSearchEl.querySelector(".valuationNumberSearch");
        this.valuationNumber = ratesSearchEl.querySelector("input[name='valuationNumber']");
        this.autoList = ratesSearchEl.querySelector(".auto-list");
        this.results = ratesSearchEl.querySelector(".results");
        this.searchBtn = ratesSearchEl.querySelector(".searchBtn");
        this.searchText = this.searchBtn?.innerHTML || '';
        this.dataResults = this.results.querySelector(".data-results");
        this.searchingEl = this.results.querySelector(".searching");
    }

    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    getCorrespondingAbbrevs(inputString) {
        if (!inputString) return [];
        const result = [];
        for (const [key, value] of Object.entries(this.abbreviations)) {
            const regex = new RegExp(`(?:^|\\s)${this.escapeRegExp(key)}(?:$|\\s)`, 'i');
            if (regex.test(inputString)) {
                result.push({ key, value });
            }
        }
        return result;
    }

    getImprovementsContent(inputString) {
        const container = document.createElement("div");
        if (!inputString || inputString === "NULL") return container;

        const abbrevs = this.getCorrespondingAbbrevs(inputString);
        if (abbrevs.length === 0) {
            container.innerText = inputString;
            return container;
        }

        const table = document.createElement("table");
        table.classList.add("abbrev", "hidden");

        abbrevs.forEach(({ key, value }) => {
            const tr = document.createElement("tr");
            const tdKey = document.createElement("td");
            tdKey.innerText = key;
            const tdValue = document.createElement("td");
            tdValue.innerText = value;
            tr.append(tdKey, tdValue);
            table.appendChild(tr);
        });

        const link = document.createElement("a");
        link.href = "#";
        link.innerText = inputString;
        link.addEventListener("click", (e) => {
            e.preventDefault();
            table.classList.toggle("hidden");
        });

        container.append(link, table);
        return container;
    }

    handleAutoComplete() {
        const items = this.addresses;
        const input = this.streetName;
        const list = this.autoList;
        if (!input || !list) return;

        let activeIndex = -1;

        input.addEventListener('input', () => {
            const query = input.value.toLowerCase().trim();
            if (!query) {
                closeDropdown();
                return;
            }
            const matches = items.filter(item => item.toLowerCase().includes(query));
            renderMatches(matches);
        });

        input.addEventListener('keydown', (e) => {
            const visibleItems = list.querySelectorAll('li');
            if (list.classList.contains('hidden') || !visibleItems.length) return;

            if (e.key === 'Escape') {
                closeDropdown();
                e.preventDefault();
            } else if (e.key === 'ArrowDown') {
                activeIndex = (activeIndex + 1) % visibleItems.length;
                setActiveItem(visibleItems);
                e.preventDefault();
            } else if (e.key === 'ArrowUp') {
                activeIndex = (activeIndex - 1 + visibleItems.length) % visibleItems.length;
                setActiveItem(visibleItems);
                e.preventDefault();
            } else if (e.key === 'Enter') {
                if (activeIndex > -1 && visibleItems[activeIndex]) {
                    selectItem(visibleItems[activeIndex].textContent);
                    e.preventDefault();
                }
            }
        });

        const renderMatches = (matches) => {
            list.replaceChildren();
            activeIndex = -1;

            if (matches.length === 0) {
                closeDropdown();
                return;
            }

            const fragment = document.createDocumentFragment();
            matches.forEach(item => {
                const li = document.createElement('li');
                li.textContent = item;
                li.setAttribute('role', 'option');
                li.addEventListener('click', () => selectItem(item));
                fragment.appendChild(li);
            });

            list.appendChild(fragment);
            list.classList.remove('hidden');
            input.setAttribute('aria-expanded', 'true');
        };

        const setActiveItem = (elements) => {
            elements.forEach((el, index) => {
                const isActive = index === activeIndex;
                el.classList.toggle('active', isActive);
                if (isActive) el.scrollIntoView({ block: 'nearest' });
            });
        };

        const selectItem = (value) => {
            input.value = value;
            closeDropdown();
            input.focus();
        };

        const closeDropdown = () => {
            list.classList.add('hidden');
            list.replaceChildren();
            activeIndex = -1;
            input.setAttribute('aria-expanded', 'false');
        };

        document.addEventListener('click', (e) => {
            if (!input.contains(e.target) && !list.contains(e.target)) {
                closeDropdown();
            }
        });
    }

    clearErrors() {
        this.ratesSearchEl.querySelectorAll(".error").forEach((el) => el.replaceChildren());
    }

    handleErrorTextClear() {
        document.addEventListener('click', () => this.clearErrors());
        document.addEventListener('keydown', () => this.clearErrors());
    }

    handleError(message) {
        this.searching = false;
        const resultsError = this.results.querySelector(".error");
        if (resultsError) resultsError.innerText = message;
    }

    async searchProperties(query = '', page = 1, pageSize = 10) {
        this.searching = true;
        this.handleSearchStatus();

        const encodedQuery = encodeURIComponent(query);
        const url = `/api/ratesapi/search/${encodedQuery}/${page}/${pageSize}`;

        try {
            const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const page2 = page * pageSize + 1;
            const url2ndPage = `/api/ratesapi/search/${encodedQuery}/${page2}/1`;
            const response2 = await fetch(url2ndPage, { headers: { 'Accept': 'application/json' } });

            if (!response2.ok) throw new Error(`HTTP error! Status: ${response2.status}`);

            return {
                data: await response.json(),
                nextPage: await response2.json()
            };
        } catch (error) {
            console.error('Error fetching property summary:', error);
            this.handleError('Rates API is currently not available. Try again later.');
        } finally {
            this.searching = false;
            this.handleSearchStatus();
        }
    }

    async getPropertyData(id) {
        try {
            const response = await fetch(`/api/ratesapi/details/${id}`, { headers: { 'Accept': 'application/json' } });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

            const dataArray = await response.json();
            if (dataArray.length > 0) return dataArray[0];

            this.handleError('Error - empty array on fetching property summary');
        } catch (error) {
            console.error('Error fetching property details:', error);
            this.handleError('Rates API is currently not available. Try again later.');
        }
    }

    isStringANumber(str) {
        return str != null && !isNaN(str) && !isNaN(parseFloat(str));
    }

    dollarFormat(value) {
        if (!this.isStringANumber(value)) return "";
        const numericAmount = parseFloat(value);
        if (numericAmount < 0.01) return "";

        const options = { style: 'currency', currency: 'NZD' };
        if (numericAmount > 100000) {
            options.minimumFractionDigits = 0;
            options.maximumFractionDigits = 0;
        }

        return new Intl.NumberFormat('en-NZ', options).format(numericAmount);
    }

    convertToStrictLongDate(dateStr) {
        if (!dateStr || typeof dateStr !== 'string' || !dateStr.trim()) return null;
        const timestamp = Date.parse(dateStr);
        if (isNaN(timestamp)) return null;

        return new Intl.DateTimeFormat('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(new Date(timestamp));
    }

    async handleDisplayProperty(id) {
        this.dataResults.classList.add("hidden");
        this.ratesDetail.classList.add("hidden");
        this.ratesLoading.classList.remove("hidden");
        this.rates.classList.remove("hidden");

        const data = await this.getPropertyData(id);
        if (!data) {
            this.rates.classList.add("hidden");
            this.dataResults.classList.remove("hidden");
            return;
        }

        this.ratesLoading.classList.add("hidden");
        const detail = this.ratesDetail;

        detail.querySelectorAll(".finyear").forEach(d => d.innerText = data.UptoTheFinancialYear || "");
        detail.querySelector(".assessment").innerText = data.AssessmentNumber || "";
        detail.querySelector(".address").innerText = data.Address || "";
        detail.querySelector(".legal-description").innerText = data.LegalDescription || "";
        detail.querySelector(".area").innerText = data.Area || "";

        const improvementsContainer = detail.querySelector(".improvements");
        improvementsContainer.replaceChildren(this.getImprovementsContent(data.ImprovementsDescription));

        const longDate = this.convertToStrictLongDate(data.AsValuedAtUTC);
        detail.querySelector(".valuedat").innerText = longDate || "";
        detail.querySelector(".land-valuation").innerText = this.dollarFormat(data.LandValuation);
        detail.querySelector(".improvement-valuation").innerText = this.dollarFormat(data.ImprovementValuation);
        detail.querySelector(".capital-valuation").innerText = this.dollarFormat(data.CapitalValue);

        const ratesSummary = Array.isArray(data.RatesSummary) && data.RatesSummary.length > 0 ? data.RatesSummary[0] : null;
        const totalNett = ratesSummary ? this.dollarFormat(ratesSummary.TotalRatesLevy) : "";
        const totalGST = ratesSummary ? this.dollarFormat(ratesSummary.TotalGST) : "";
        const totalLevy = ratesSummary ? this.dollarFormat(ratesSummary.Total) : "";

        detail.querySelector(".nett").innerText = totalNett;
        detail.querySelector(".gst").innerText = totalGST;
        detail.querySelector(".total-levy").innerText = totalLevy;

        // Render Rates Table
        const ratesTable = detail.querySelector(".rates-table");
        const tbody = ratesTable.querySelector("tbody");
        tbody.replaceChildren();
        ratesTable.classList.add("hidden");

        if (data.Rates?.length > 0) {
            const sortedRates = [...data.Rates].sort((a, b) => a.ProductCode.localeCompare(b.ProductCode));
            const fragment = document.createDocumentFragment();

            sortedRates.forEach(rate => {
                const tr = document.createElement("tr");
                tr.innerHTML = `
                    <td>${this.htmlEncode(rate.ProductCode)}</td>
                    <td>${this.htmlEncode(rate.ProductName)}</td>
                    <td>${this.htmlEncode(rate.Units)}</td>
                    <td>${Number(rate.Charge) || ''}</td>
                    <td>${this.dollarFormat(rate.ExcAmt)}</td>
                    <td>${this.dollarFormat(rate.GST)}</td>
                    <td>${this.dollarFormat(rate.IncAmt)}</td>
                `;
                fragment.appendChild(tr);
            });

            const totalTr = document.createElement("tr");
            totalTr.innerHTML = `<th colspan="4">Total</th><th>${totalNett}</th><th>${totalGST}</th><th>${totalLevy}</th>`;
            fragment.appendChild(totalTr);

            tbody.appendChild(fragment);
            ratesTable.classList.remove("hidden");
        }

        // Render Instalments Table
        const instalmentsTable = detail.querySelector(".instalments-table");
        const tbodyInstalments = instalmentsTable.querySelector("tbody");
        tbodyInstalments.replaceChildren();
        instalmentsTable.classList.add("hidden");

        if (data.Instalments?.length > 0) {
            const fragment = document.createDocumentFragment();
            data.Instalments.forEach(instalment => {
                const tr = document.createElement("tr");
                tr.innerHTML = `<td>${this.convertToStrictLongDate(instalment.DueDateUTC)}</td><td>${this.dollarFormat(instalment.Amount)}</td>`;
                fragment.appendChild(tr);
            });
            const totalTr = document.createElement("tr");
            totalTr.innerHTML = `<th>Total</th><th>${totalLevy}</th>`;
            fragment.appendChild(totalTr);

            tbodyInstalments.appendChild(fragment);
            instalmentsTable.classList.remove("hidden");
        }

        // Render Future Table
        const futureTable = detail.querySelector(".future-table");
        if (futureTable) {
            const hideInclusiveAssessed = futureTable.dataset.hideinclusiveassessed === "true";
            if (hideInclusiveAssessed) {
                futureTable.querySelector(".inclusiveAssessed")?.remove();
            }


            const tbodyFuture = futureTable.querySelector("tbody");
            tbodyFuture.replaceChildren();
            futureTable.classList.add("hidden");

            if (data.FutureValuations?.length > 0) {
                const fragment = document.createDocumentFragment();
                data.FutureValuations.forEach(item => {
                    const tr = document.createElement("tr");
                    tr.innerHTML = `
                    <td>${this.htmlEncode(item.AssessmentYear)}</td>
                    <td>${this.dollarFormat(item.LandValuation)}</td>
                    <td>${this.dollarFormat(item.ImprovementValuation)}</td>
                    <td>${this.dollarFormat(item.CapitalValue)}</td>
                    ${hideInclusiveAssessed ? '' : `<td>${this.dollarFormat(item.InclusiveAssessedAmount)}</td>`}
                `;
                    fragment.appendChild(tr);
                });
                tbodyFuture.appendChild(fragment);
                futureTable.classList.remove("hidden");
            }
        }

        detail.classList.remove("hidden");
        detail.scrollIntoView({ behavior: 'smooth' });
    }

    handleSearchResults(data) {
        this.searchingEl.classList.add("hidden");

        if (!data?.data?.length) {
            this.handleError(this.noitems);
            return;
        }

        this.prev.classList.toggle("hidden", this.next.dataset.page === "1");
        this.next.classList.toggle("hidden", !(data.nextPage && data.nextPage.length > 0));

        this.form.classList.add("hidden");
        const tableBody = this.dataResults.querySelector("tbody");
        tableBody.replaceChildren();

        const fragment = document.createDocumentFragment();
        data.data.slice(0, 10).forEach(dataItem => {
            const tr = document.createElement("tr");
            tr.innerHTML = `
                <td><a href="./?id=${dataItem.PropertyID}" data-id="${dataItem.PropertyID}">${this.htmlEncode(dataItem.ValuationNumber)}</a></td>
                <td>${this.htmlEncode(dataItem.LegalDescription)}</td>
                <td>${this.htmlEncode(dataItem.FormattedAddress)}</td>
            `;
            fragment.appendChild(tr);
        });

        tableBody.appendChild(fragment);

        tableBody.querySelectorAll("a").forEach(l => {
            l.addEventListener("click", (e) => {
                e.preventDefault();
                this.handleDisplayProperty(e.target.dataset.id);
            });
        });

        this.dataResults.classList.remove("hidden");
        this.dataResults.scrollIntoView({ behavior: 'smooth' });
    }

    htmlEncode(str) {
        return String(str ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    async searchStreet(page) {
        this.next.dataset.page = page || 1;
        const streetNum = this.streetNumber.value.trim();
        const streetName = this.streetName.value.trim();
        const query = streetNum ? `${streetNum} ${streetName}` : streetName;

        if (query) {
            this.dataResults.querySelector(".query").innerText = `Search: ${query}, Page: ${this.next.dataset.page}`;
            const data = await this.searchProperties(query, page);
            this.handleSearchResults(data);
        } else {
            this.streetSearch.querySelector(".error").innerText = "Please enter a value before searching";
        }
    }

    async searchValuation(page) {
        this.next.dataset.page = page || 1;
        const query = this.valuationNumber.value.trim();

        if (query) {
            this.dataResults.querySelector(".query").innerText = `Search: ${query}, Page: ${this.next.dataset.page}`;
            const data = await this.searchProperties(query, page);
            this.handleSearchResults(data);
        } else {
            this.valuationNumberSearch.querySelector(".error").innerText = "Please enter a value before searching";
        }
    }

    handleSearchBy() {
        this.searchBy.addEventListener("change", (s) => {
            const isStreet = s.target.value === "street";
            this.streetSearch.classList.toggle("hidden", !isStreet);
            this.valuationNumberSearch.classList.toggle("hidden", isStreet);
        });
    }

    handleSearchStatus() {
        if (this.searching) {
            this.searchBtn.innerHTML = "...";
            this.searchBtn.setAttribute("disabled", "disabled");
        } else {
            this.searchBtn.innerHTML = this.searchText;
            this.searchBtn.removeAttribute("disabled");
        }
    }

    handleSubmission() {
        this.dataResults.classList.add("hidden");
        this.form.addEventListener("submit", (e) => {
            e.preventDefault();
            this.searchBy.value === "street" ? this.searchStreet() : this.searchValuation();
        });
    }

    handleRefine() {
        this.refine.addEventListener("click", (e) => {
            e.preventDefault();
            this.rates.classList.add("hidden");
            this.dataResults.classList.add("hidden");
            this.form.classList.remove("hidden");
            this.form.closest(".grid")?.scrollIntoView({ behavior: 'smooth' });
        });
    }

    handleBack() {
        this.back.addEventListener("click", (e) => {
            e.preventDefault();
            this.rates.classList.add("hidden");
            this.dataResults.classList.remove("hidden");
            this.form.classList.add("hidden");
        });
    }

    doNav(page) {
        this.dataResults.classList.add("hidden");
        this.searchingEl.classList.remove("hidden");
        this.searchBy.value === "street" ? this.searchStreet(page) : this.searchValuation(page);
        this.results.scrollIntoView({ behavior: 'smooth' });
    }

    handleNav() {
        this.next.addEventListener("click", (e) => {
            e.preventDefault();
            this.doNav(parseInt(this.next.dataset.page, 10) + 1);
        });
        this.prev.addEventListener("click", (e) => {
            e.preventDefault();
            this.doNav(parseInt(this.next.dataset.page, 10) - 1);
        });
    }

    init() {
        this.handleAutoComplete();
        this.handleSearchBy();
        this.handleSubmission();
        this.handleErrorTextClear();
        this.handleRefine();
        this.handleBack();
        this.handleNav();
    }
}